import type {
  Kpi,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import {
  GROUP_COLUMNS_BY,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';

export interface ColumnSettings<T extends string = string> {
  hidden: T[];
  order: T[];
}

export function defaultColumnSettings<T extends string>(
  ids: readonly T[],
): ColumnSettings<T> {
  return { order: [...ids], hidden: [] };
}

export function getVisibleKpis({ hidden, order }: ColumnSettings<Kpi>): Kpi[] {
  return order.filter((kpi) => !hidden.includes(kpi));
}

export function getVisibleMetricTypes(
  { hidden, order }: ColumnSettings,
  { groupColumnsBy }: TableLayoutPreferences,
  metrics: Record<string, MetricType>,
  fixed?: MetricType,
): MetricType[] {
  const fixedColumns = fixed ? [fixed] : [];

  // KPIs mode ignores category settings: the fixed column + every category,
  // default order (the record's insertion order).
  if (groupColumnsBy === GROUP_COLUMNS_BY.KPIS) {
    return [...fixedColumns, ...Object.values(metrics)];
  }

  return [
    ...fixedColumns,
    ...order
      .filter((column) => !hidden.includes(column))
      // Ids are validated on read (`parseColumnSettings`), so each has a metric.
      .flatMap((column) => metrics[column] ?? []),
  ];
}

/**
 * Validates stored column settings against the currently available `ids`:
 * unknown and duplicate ids are dropped, missing ids are appended to the order.
 * Without a `fixed` column, hiding every id is treated as hiding none.
 */
export function parseColumnSettings<T extends string>(
  json: unknown,
  ids: readonly T[],
  fixed?: MetricType,
): ColumnSettings<T> {
  if (typeof json !== 'object' || json === null) {
    return defaultColumnSettings(ids);
  }

  const raw = json as Record<string, unknown>;
  const known = (value: unknown): T[] =>
    Array.isArray(value)
      ? [
          ...new Set(
            (value as unknown[]).filter((id): id is T => ids.includes(id as T)),
          ),
        ]
      : [];

  const order = known(raw.order);
  const hidden = known(raw.hidden);

  return {
    order: [...order, ...ids.filter((id) => !order.includes(id))],
    hidden: !fixed && hidden.length === ids.length ? [] : hidden,
  };
}
